import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);
const IDLE_MS = 15 * 60 * 1000; // 15 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, obj=logged in
  const idleTimer = useRef(null);

  const logout = useCallback(() => {
    localStorage.removeItem("kh_token");
    localStorage.removeItem("kh_last_activity");
    setUser(false);
  }, []);

  const resetIdle = useCallback(() => {
    localStorage.setItem("kh_last_activity", String(Date.now()));
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => logout(), IDLE_MS);
  }, [logout]);

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem("kh_token");
    const last = parseInt(localStorage.getItem("kh_last_activity") || "0", 10);
    if (!token) { setUser(false); return; }
    if (last && Date.now() - last > IDLE_MS) { logout(); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      resetIdle();
    } catch {
      logout();
    }
  }, [logout, resetIdle]);

  useEffect(() => { checkSession(); }, [checkSession]);

  // Track activity
  useEffect(() => {
    if (!user) return;
    const handler = () => resetIdle();
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdle();
    return () => events.forEach((e) => window.removeEventListener(e, handler));
  }, [user, resetIdle]);

  const login = async (identifier, password) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      localStorage.setItem("kh_token", data.token);
      localStorage.setItem("kh_last_activity", String(Date.now()));
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
